import React, { forwardRef, useRef, useState } from 'react';
import Label from '../Label';
import Input from '../Input';
import InputIcon from '../InputIcon';
import FakeLabel from '../FakeLabel';
import FakeInput from '../FakeInput';
import { StyledFormGroup } from '../../styles/helpers.styles';
import { Error, InputHolder } from './Field.styles';
import DatePicker from '../DatePicker';
import { TbCheck } from 'react-icons/tb';
import { Eye, EyeOff } from 'lucide-react';
import UploadFile from '../UploadFile';
import PhoneNumberInput from '../PhoneNumberInput';
import Select from '../Select';
const defaultProps = {
  type: 'text',
};

const Field = forwardRef(
  (
    {
      rules,
      error,
      name,
      invalid,
      label,
      type,
      prefix,
      suffix,
      rounded,
      noMargin,
      margin,
      button,
      searchField,
      onlyRead,
      labelIcon,
      disabled,
      datePicker,
      clear,
      labelReverse,
      radioBorder,
      labelColor,
      onAutoPasscodeGenerate,
      upload,
      customHeight,
      ...props
    },
    ref,
  ) => {
    const [isRevealPwd, setIsRevealPwd] = useState(false);
    const passcodeRef = useRef(null);

    const inputProps = {
      id: props.id ?? name,
      name,
      type,
      invalid,
      'aria-describedby': `${name}Error`,
      ...props,
    };
    const renderInputFirst = type === 'checkbox' || type === 'radio';
    return (
      <StyledFormGroup
        $invalid={invalid || error}
        noMargin={noMargin}
        $customHeight={customHeight}
        className="field-input-wrap"
        css={`
          margin-bottom: ${margin};
        `}>
        {renderInputFirst && label && (
          <Label
            htmlFor={inputProps.id}
            labelIcon={labelIcon}
            onlyRead={onlyRead}
            clear={clear}
            labelReverse={labelReverse}
            css="display: flex !important; align-items:center; margin-bottom:0 !important;">
            <Input
              {...inputProps}
              ref={ref}
              disabled={disabled}
              $invalid={invalid || error}
              checked={inputProps?.value}
              // eslint-disable-next-line no-shadow
              onChange={({ target: { name, checked } }) => inputProps?.onChange?.({ target: { name, value: checked } })}
            />
            <FakeInput $radioBorder={radioBorder} $labelReverse={labelReverse}>
              {type === 'checkbox' && <TbCheck color="var(--white)" />}
            </FakeInput>
            <FakeLabel $labelColor={labelColor} required={rules?.filter(({ required }) => required).length}>
              {label}
            </FakeLabel>
          </Label>
        )}

        {renderInputFirst || (
          <>
            {label && (
              <Label
                onClear={() =>
                  inputProps?.onChange?.({
                    target: {
                      name,
                      value: type === 'datepicker' ? [null, null] : '',
                    },
                  })
                }
                clear={clear}
                labelIcon={labelIcon}
                htmlFor={inputProps.id}
                required={rules?.filter(({ required }) => required).length}>
                {label}
              </Label>
            )}
            <InputHolder $customHeight={customHeight} $searchField={searchField}>
              {/* input left icon */}
              {prefix && (
                <InputIcon
                  disabled={disabled}
                  // as={type === 'search' && 'button'}
                  // type={type === 'search' ? 'button' : undefined}
                  prefix={prefix}
                  invalid={invalid || error}
                  css={type === 'search' && 'color: var(--blue); font-size: 25px; left: 11px;'}>
                  {prefix}
                </InputIcon>
              )}
              {suffix && (
                <InputIcon suffix={suffix} disabled={disabled} invalid={invalid || error}>
                  {suffix}
                </InputIcon>
              )}
              {/* {datePicker && <DatePicker inputProps={inputProps} />} */}
              {/* password field */}
              {type === 'password' ? (
                <>
                  <Input
                    ref={ref}
                    {...inputProps}
                    $prefix={prefix}
                    $suffix={suffix}
                    $invalid={invalid || error}
                    type={isRevealPwd ? 'text' : 'password'}
                    $rounded={rounded}
                    disabled={disabled}
                    $button={button && true}
                    // autoComplete="off"
                    autoComplete="new-password"
                  />
                  <InputIcon
                    disabled={disabled}
                    suffix
                    css="cursor: pointer"
                    onClick={() => setIsRevealPwd(prevState => !prevState)}>
                    {isRevealPwd ? (
                      <EyeOff size={20} strokeWidth={1.75} />
                    ) : (
                      <Eye size={20} strokeWidth={1.75} />
                    )}
                  </InputIcon>
                </>
              ) : type === 'datepicker' ? (
                <DatePicker {...inputProps} prefix={prefix} $invalid={invalid || error} />
              ) : type === 'select' ? (
                <Select {...inputProps} $invalid={invalid || error} noMargin />
              ) : type === 'phone' ? (
                <PhoneNumberInput
                  {...inputProps}
                  prefix={prefix}
                  disabled={disabled}
                  $invalid={invalid || error}
                  noMargin
                />
              ) : type === 'passcode' ? (
                <>
                  <Input
                    ref={ref || passcodeRef}
                    {...inputProps}
                    $prefix={prefix}
                    $suffix={suffix}
                    $invalid={invalid || error}
                    $rounded={rounded}
                    disabled={disabled}
                  />
                  <InputIcon suffix style={{ cursor: 'pointer' }} onClick={onAutoPasscodeGenerate}>
                    <span className="material-icons-outlined">sync</span>
                  </InputIcon>
                </>
              ) : type === 'img' ? (
                <UploadFile {...inputProps} $invalid={invalid || error} upload={upload} type={type} />
              ) : type === 'csv' ? (
                <UploadFile {...inputProps} $invalid={invalid || error} upload={upload} type={type} />
              ) : (
                <>
                  {/* any other input type */}
                  <Input
                    $customHeight
                    ref={ref}
                    {...inputProps}
                    $prefix={prefix}
                    disabled={disabled}
                    $suffix={suffix}
                    $invalid={invalid || error}
                    $rounded={rounded}
                    $button={button && true}
                  />
                  {/* input right icon */}
                  {suffix && (
                    <InputIcon suffix={suffix} disabled={disabled} invalid={invalid || error}>
                      {suffix}
                    </InputIcon>
                  )}
                  {button && (
                    <div
                      css={`
                        position: absolute;
                        top: 50%;
                        transform: translateY(-50%);
                        right: 10px;
                      `}>
                      {button}
                    </div>
                  )}
                </>
              )}
            </InputHolder>
          </>
        )}
        {invalid ||
          (error && (
            <Error id={`${name}Error`} role="alert">
              {error}
            </Error>
          ))}
      </StyledFormGroup>
    );
  },
);

Field.defaultProps = defaultProps;

export default Field;
